import LoadingImg from '@/assets/icons/loading-button.svg';
import nodataImg from '@/assets/icons/noagentdata.svg';
import successIcon from '@/assets/icons/success_circle.svg';
import AgentImg from '@/assets/images/userAgentDefault.svg';
import { errorAlert, successAlert } from '@/components/others/ToastGroup';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Web3DefaiProgramInteraction } from '@/program/web3DefaiLaunchpad';
import {
  createUpcomingToken,
  getCurrentUpcomingTokenByUser,
} from '@/utils/apiRequest/upcoming';
import { uploadImage } from '@/utils/fileUpload';
import { CoinInfo, CreateCoinInfo, MetadataInfo } from '@/utils/types';
import {
  BACKEND_URL,
  genTokenKeypair,
  getAgentsDataByUser,
  getCoinsInfoBy,
  getUserByWalletAddress,
  reduceString,
} from '@/utils/util';
import { CoinType } from '@/zustand-store/coin/useCoinStore';
import { useWallet } from '@solana/wallet-adapter-react';
import { Keypair } from '@solana/web3.js';
import base58 from 'bs58';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import DropzoneFile from '../uploadFile/DropzoneFile';
import { STEP_TOKEN_CREATION, STEP_TYPE } from './config';
import ConfirmModal from './Confirm';
import CreationStep from './CreationStep';
import LoadingCreateCoin from './LoadingCreateCoin';
import SetupInvestmentStrategy from './SetupInvestmentStrategy';
import WaitingForUpcomingToken from './WaitingForUpcomingToken';

const web3Defai = new Web3DefaiProgramInteraction();

export default function CreateToken() {
  const isDevnet = ['devnet', 'mainnet-beta-test'].includes(
    import.meta.env.VITE_APP_SOLANA_ENV,
  );
  const [imageUrl, setIamgeUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfirm, setIsLoadingConfirm] = useState(false);
  const [createdTokenSuccessful, setCreatedTokenSuccessful] = useState<any>();
  const [showOptional, setShowOptional] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [mintKp, setMintKp] = useState<Keypair>();
  const [newCoin, setNewCoin] = useState<CreateCoinInfo>({} as CreateCoinInfo);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [getAmt, setGetAmt] = useState<number>(0);
  const [agentList, setAgentList] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectGroup, setSelectedGroup] = useState(false);
  const refAgent = useRef();
  const [errors, setErrors] = useState({
    name: '',
    ticker: '',
    description: '',
    image: '',
    curve: '',
    partyRoundTime: '',
    interval: '',
  });
  const [creatingToken, setCreatingToken] = useState<any>();
  const wallet = useWallet();
  const [unCreatableToken, setUnCreatableToken] = useState<CoinInfo[]>([]);
  const [policiesList, setPolicyList] = useState<string[]>([]);
  const [stepCreate, setStepCreate] = useState<STEP_TYPE>(
    STEP_TOKEN_CREATION[0],
  );

  const checkAgentTokenized = (agent) => {
    try {
      const coinCreateByAgent = unCreatableToken.filter(
        (tk) => tk.metadata?.agentAddress === agent?.botWallet?.solAddr,
      );

      let checkTokenDefaiHasStrategy = false;
      coinCreateByAgent.map((tk) => {
        const typeToken = tk?.metadata?.tags;

        const coinDefaiHasStrategy =
          tk && typeToken === CoinType.DEFAI && policiesList.includes(tk.token);

        if (coinDefaiHasStrategy) {
          checkTokenDefaiHasStrategy = true;
        }
      });

      const agentTokenized =
        selectedAgent?.typeAgent === 1
          ? checkTokenDefaiHasStrategy
          : !!coinCreateByAgent?.[0];

      return agentTokenized;
    } catch (error) {
      console.log('error check tokenized', error);
    }
  };

  const fetchCreatedCoin = async (userAddress: string) => {
    try {
      const response = await getUserByWalletAddress({
        baseUrl: BACKEND_URL,
        wallet: userAddress,
      });
      const userId = response?._id;

      if (!userId) return;
      const coinsBy = await getCoinsInfoBy(BACKEND_URL, userId);
      if (coinsBy) {
        setUnCreatableToken(coinsBy);
        return coinsBy;
      }
    } catch (error) {
      console.log('Error fetching coins:', error);
    }
  };

  const fetchPolicyList = async () => {
    try {
      const listPolicies = ((await web3Defai.getPolicyDefList()) || [])
        .map((e) => e?.agentToken?.toBase58())
        .filter(Boolean);

      const uniqueList = [...new Set(listPolicies)];
      setPolicyList(uniqueList);
    } catch (error) {
      console.log('error get List policy', error);
    }
  };

  useEffect(() => {
    fetchPolicyList();
  }, []);

  useEffect(() => {
    if (wallet.publicKey) {
      fetchCreatedCoin(wallet.publicKey.toBase58());
    }
  }, [wallet.publicKey]);

  useOnClickOutside(refAgent, () => {
    setSelectedGroup(false);
  });

  useEffect(() => {
    if (wallet.publicKey) {
      (async () => {
        const res = await getAgentsDataByUser({ user: wallet.publicKey });

        if (res) {
          setAgentList(res['items']);
          if (isDevnet) {
            setSelectedAgent(res['items'][0]); // TODO: only on test
          }
        }
      })();
    }
  }, [wallet.publicKey]);

  useEffect(() => {
    if (wallet.publicKey) {
      (async () => {
        const res = await getCurrentUpcomingTokenByUser(
          wallet.publicKey?.toBase58(),
        );

        if (res) {
          console.log('CreatingToken', res);
          setCreatingToken(res);
          setStepCreate(STEP_TOKEN_CREATION[1]);
        }
      })();
    }
  }, [wallet.publicKey]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setNewCoin({ ...newCoin, [e.target.id]: e.target.value });
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    console.log('Accepted files:', acceptedFiles);
    const file = acceptedFiles?.[0];
    if (file) {
      setSelectedFileName(file.name);
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setIamgeUrl(URL.createObjectURL(file));
    }
  }, []);

  useEffect(() => {
    validateFormToken();
    // validateFormDCA();
  }, [newCoin, imageUrl]);
  // curve, party, dcaConfig, fundExp

  const validateFormToken = () => {
    const validationErrors = {
      name: !newCoin.name ? 'Token name is required.' : '',
      ticker: !newCoin.ticker ? 'Token ticker is required.' : '',
      description: !newCoin.description ? 'Token description is required.' : '',
      image: !imageUrl ? 'Token image is required.' : '',
    };

    setErrors((e) => {
      return {
        ...e,
        ...validationErrors,
      };
    });

    const errorMessages = Object.values(validationErrors).filter(Boolean);
    if (errorMessages.length > 0) {
      // errorMessages.forEach((message) => errorAlert(message));
      return false;
    }

    return true;
  };

  const formValid =
    newCoin.name &&
    newCoin.proposalLink &&
    newCoin.ticker &&
    newCoin.description &&
    imageUrl &&
    selectedAgent;

  const agentTokenized = checkAgentTokenized(selectedAgent);
  const isSelectedAgentHasCoin = !isDevnet && agentTokenized;

  const submitUpcoming = async () => {
    try {
      const textSign = 'Confirm to create upcoming token!';
      const msg = new TextEncoder().encode(`${textSign}`);

      const sig = await wallet.signMessage?.(msg);
      const signature = base58.encode(sig as Uint8Array);

      setIsLoading(true);
      // Process image upload
      const uploadedImageUrl = await uploadImage(imageUrl);
      if (!uploadedImageUrl) {
        errorAlert('Image upload failed.');
        setIsLoading(false);
        return;
      }
      const isDefai = selectedAgent?.typeAgent === 1;

      const jsonData: MetadataInfo = {
        name: newCoin.name,
        symbol: newCoin.ticker,
        image: uploadedImageUrl,
        description: newCoin.description,
        createdOn: 'https://agents.land',
        twitter: newCoin.twitter || undefined, // Only assign if it exists
        website: newCoin.website || undefined, // Only assign if it exists
        telegram: newCoin.telegram || undefined, // Only assign if it exists
        discord: newCoin.discord || undefined, // Only assign if it exists
        agentId: selectedAgent?.id,
        agentClanId: selectedAgent?.ownedGroups?.[0]?.label || '', //
        agentAddress: selectedAgent?.botWallet?.solAddr,
        creatorAddress: wallet.publicKey?.toBase58(),
        createdAt: Date.now(),
        tags: selectedAgent?.typeAgent === 1 ? CoinType.DEFAI : CoinType.AGENT,
        mode: null,
        isIdoLaunch: isDefai,
      };

      const data = {
        creator: wallet.publicKey?.toBase58(),
        proposalLink: newCoin.proposalLink,
        name: newCoin.name,
        ticker: newCoin.ticker,
        description: newCoin.description,
        url: uploadedImageUrl,
        metadata: jsonData,
        signature,
        reason: textSign,
      };

      const res = await createUpcomingToken(data);

      if (res) {
        successAlert('Create upcoming token successfully');
        setCreatingToken(res);
        setStepCreate(STEP_TOKEN_CREATION[1]);
      }
    } catch (error) {
      console.log('error upcoming coin', error);
      errorAlert('Payment failed or was rejected.');
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const onLaunch = async () => {
    try {
      setIsLoadingConfirm(true);

      const envMode = import.meta.env.VITE_APP_SOLANA_ENV;
      let mintKp = Keypair.generate();
      if (envMode === 'mainnet-beta') {
        console.log('gen Orai prefix');
        const key = await genTokenKeypair(BACKEND_URL);
        mintKp = Keypair.fromSecretKey(base58.decode(key));
      }
      console.log('tokenAddress:', mintKp.publicKey.toBase58());

      setMintKp(mintKp);
      setShowConfirm(true);
    } catch (error) {
      console.log('error gen token address', error);
      errorAlert('Failed to gen token address. Try again!');
    } finally {
      setIsLoadingConfirm(false);
    }
  };

  if (createdTokenSuccessful) {
    return (
      <div className="w-full gap-4 md:gap-6 flex flex-col items-center justify-center m-auto py-24 pt-4 md:pt-10">
        <img
          src={successIcon}
          alt="successIcon"
          className="w-10 h-10 rounded-full mt-2"
        />
        <div className="text-center">
          <p className="text-[#FCFCFC] text-[32px]">
            Token launched successfully!
          </p>
          <div className="text-[#9192A0] mt-2">
            {createdTokenSuccessful.name} (${createdTokenSuccessful.symbol})
          </div>
        </div>
        <div className="w-full max-w-[150px]">
          <a
            href={`/trading/${createdTokenSuccessful.token.toString()}`}
            target="_blank"
            className="flex disabled:opacity-75 w-full disabled:cursor-not-allowed uppercase p-1 rounded border-[2px] border-solid border-[rgba(255,255,255,0.25)] cursor-pointer hover:border-[rgba(255,255,255)] transition-all ease-in duration-150"
          >
            <div className="w-full text-center uppercase rounded bg-white px-6 py-2 text-[#080A14]">
              View
            </div>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[596px] m-auto py-24 pt-4 md:pt-10">
      {/* <PreSaleModal
        isOpen={showModalPreSale}
        closeModal={() => setShowModalPreSale(false)}
        onConfirm={createCoin}
        quantity={newCoin.presale}
        setQuantity={handlePresaleChange}
      /> */}
      {/* <CreateTokenSuccess
        isOpen={showModalSuccess}
        coin={coinCreatedData}
        closeModal={() => setShowModalSuccess(false)}
      /> */}
      <ConfirmModal
        isOpen={showConfirm}
        coin={{
          name: newCoin.name,
          symbol: newCoin.ticker,
          uri: imageFile,
          token: mintKp?.publicKey?.toBase58(),
        }}
        closeModal={() => setShowConfirm(false)}
        onConfirm={async () => {
          // createCoin();
          submitUpcoming();
          setShowConfirm(false);
        }}
        hideCA
      />
      <CreationStep step={stepCreate} setStep={(step) => setStepCreate(step)} />

      {isLoading && (
        <LoadingCreateCoin isLoading={isLoading} setIsLoading={setIsLoading} />
      )}
      <div className="flex flex-col justify-center items-start pb-10">
        {stepCreate.id === STEP_TOKEN_CREATION[0].id && (
          <div className="w-full flex flex-col gap-6 bg-[#13141D] rounded-lg p-4 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="text-[12px] font-medium text-[#84869A] flex items-center cursor-pointer"
                  >
                    AGENT&nbsp;<span className="text-red-700">*</span>
                    <div className="group relative cursor-pointer">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        className="ml-1"
                      >
                        <path
                          d="M6 1.3125C3.41531 1.3125 1.3125 3.41531 1.3125 6C1.3125 8.58469 3.41531 10.6875 6 10.6875C8.58469 10.6875 10.6875 8.58469 10.6875 6C10.6875 3.41531 8.58469 1.3125 6 1.3125ZM6 3.23438C6.12052 3.23438 6.23834 3.27011 6.33855 3.33707C6.43876 3.40403 6.51687 3.4992 6.56299 3.61055C6.60911 3.7219 6.62118 3.84443 6.59767 3.96263C6.57415 4.08084 6.51612 4.18942 6.43089 4.27464C6.34567 4.35987 6.23709 4.4179 6.11888 4.44142C6.00068 4.46493 5.87815 4.45286 5.7668 4.40674C5.65545 4.36062 5.56028 4.28251 5.49332 4.1823C5.42636 4.08209 5.39062 3.96427 5.39062 3.84375C5.39062 3.68213 5.45483 3.52714 5.56911 3.41286C5.68339 3.29858 5.83838 3.23438 6 3.23438ZM7.125 8.53125H5.0625C4.96304 8.53125 4.86766 8.49174 4.79734 8.42142C4.72701 8.35109 4.6875 8.25571 4.6875 8.15625C4.6875 8.05679 4.72701 7.96141 4.79734 7.89108C4.86766 7.82076 4.96304 7.78125 5.0625 7.78125H5.71875V5.71875H5.34375C5.24429 5.71875 5.14891 5.67924 5.07859 5.60891C5.00826 5.53859 4.96875 5.44321 4.96875 5.34375C4.96875 5.24429 5.00826 5.14891 5.07859 5.07859C5.14891 5.00826 5.24429 4.96875 5.34375 4.96875H6.09375C6.19321 4.96875 6.28859 5.00826 6.35892 5.07859C6.42924 5.14891 6.46875 5.24429 6.46875 5.34375V7.78125H7.125C7.22446 7.78125 7.31984 7.82076 7.39017 7.89108C7.46049 7.96141 7.5 8.05679 7.5 8.15625C7.5 8.25571 7.46049 8.35109 7.39017 8.42142C7.31984 8.49174 7.22446 8.53125 7.125 8.53125Z"
                          fill="#585A6B"
                        />
                      </svg>

                      <div className="pb-2 invisible group-hover:visible absolute bottom-full w-screen max-w-[210px]">
                        <div className="bg-[#30344A] shadow shadow-[rgba(0,_0,_0,_0.10)] p-3 text-[12px] text-[#F7F7F7] rounded-lg">
                          Private intellegent created from{' '}
                          <a
                            href="https://mesh.distilled.ai"
                            target="_blank"
                            className="hover:underline"
                          >
                            mesh.distilled.ai
                          </a>
                        </div>
                      </div>
                    </div>
                  </label>

                  <div
                    ref={refAgent}
                    className="relative cursor-pointer mt-3"
                    onClick={() => {
                      setSelectedGroup(!selectGroup);
                    }}
                  >
                    <div className="flex items-center justify-between w-full px-3 border border-[#585A6B] rounded h-12">
                      <div className="flex items-center">
                        {selectedAgent?.avatar ? (
                          <img
                            src={selectedAgent?.avatar}
                            alt="agentImg"
                            width={32}
                            height={32}
                            className="border-[1.5px] w-8 h-8 object-cover border-[#ADADAD] rounded-full"
                          />
                        ) : (
                          <img
                            src={AgentImg}
                            alt="agentImg"
                            width={32}
                            height={32}
                            className="border-[1.5px] w-8 h-8 object-cover border-[#ADADAD] rounded-full"
                          />
                        )}

                        <div className="text-[#E8E9EE] text-[14px] font-medium ml-[10px]">
                          {selectedAgent?.username || '--'}
                        </div>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M19.2071 8.24992C18.8166 7.8594 18.1834 7.8594 17.7929 8.24992L12 14.0428L6.20711 8.24992C5.81658 7.8594 5.18342 7.8594 4.79289 8.24992C4.40237 8.64044 4.40237 9.27361 4.79289 9.66413L10.5858 15.457C11.3668 16.2381 12.6332 16.2381 13.4142 15.457L19.2071 9.66414C19.5976 9.27361 19.5976 8.64045 19.2071 8.24992Z"
                          fill="#9192A0"
                        />
                      </svg>
                    </div>
                    {selectGroup && (
                      <div className="pt-2 absolute top-full w-full z-10">
                        <div className="bg-[#1A1C28] shadow shadow-[rgba(0,_0,_0,_0.10)] p-3 text-[12px] text-[#F7F7F7] rounded-lg flex flex-col gap-2 overflow-y-auto max-h-52 h-100%">
                          {agentList.length <= 0 ? (
                            <div className="w-full mt-4 rounded-lg bg-[#13141D] border border-dashed border-[#30344A] py-4 px-8 flex flex-col justify-center items-center">
                              <img src={nodataImg} alt="nodata" />
                              <p className="mt-4 text-[#E8E9EE] text-[16px] uppercase">
                                No Agent
                              </p>
                              <a
                                href="https://mesh.distilled.ai"
                                target="_blank"
                                className="mt-2 text-[#585A6B] text-[14px] hover:underline"
                              >
                                mesh.distilled.ai
                              </a>
                            </div>
                          ) : (
                            agentList.map((e, ind) => {
                              const agentTokenized = checkAgentTokenized(e);

                              return (
                                <button
                                  // disabled={!!agentHasCoin}
                                  className={twMerge(
                                    'flex items-center justify-between rounded-lg hover:bg-[#13141D] p-3',
                                    e?.botWallet?.solAddr ===
                                      selectedAgent?.botWallet?.solAddr &&
                                      'bg-[#13141D]',
                                  )}
                                  key={`agent-item-${ind}`}
                                  onClick={() => setSelectedAgent(e)}
                                >
                                  <div className="flex items-center">
                                    {typeof e.img === 'string' ? (
                                      <img
                                        src={e.avatar as any}
                                        alt="agentImg"
                                        width={32}
                                        height={32}
                                        className="border-[1.5px] w-8 h-8 object-cover border-[#ADADAD] rounded-full"
                                      />
                                    ) : (
                                      <img
                                        src={e.avatar as any}
                                        alt="agentImg"
                                        width={32}
                                        height={32}
                                        className="border-[1.5px] w-8 h-8 object-cover border-[#ADADAD] rounded-full"
                                      />
                                    )}
                                    <div className="text-[#E8E9EE] text-left text-[14px] font-medium ml-[10px]">
                                      <p>{e.username}</p>
                                      <p className="text-[#585A6B] text-[12px] font-medium text-left">
                                        {reduceString(
                                          e?.botWallet?.solAddr,
                                          4,
                                          4,
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <>
                                    {agentTokenized && (
                                      <div className="flex items-center text-[#585A6B] text-[12px] font-medium ">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="12"
                                          height="12"
                                          viewBox="0 0 12 12"
                                          fill="none"
                                        >
                                          <path
                                            d="M9.75 3L4.5 9L2.25 6.75"
                                            stroke="#9192A0"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                          />
                                        </svg>
                                        <span className="ml-1">Tokenized</span>
                                      </div>
                                    )}
                                  </>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-6">
                  <div className="flex-1 w-full">
                    <label
                      htmlFor="name"
                      className="text-[12px] font-medium text-[#84869A] flex items-center"
                    >
                      <span className="uppercase">
                        proposal lINK &nbsp;
                        <span className="text-red-700">*</span>
                      </span>
                      <div className="group relative cursor-pointer">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          className="ml-1"
                        >
                          <path
                            d="M6 1.3125C3.41531 1.3125 1.3125 3.41531 1.3125 6C1.3125 8.58469 3.41531 10.6875 6 10.6875C8.58469 10.6875 10.6875 8.58469 10.6875 6C10.6875 3.41531 8.58469 1.3125 6 1.3125ZM6 3.23438C6.12052 3.23438 6.23834 3.27011 6.33855 3.33707C6.43876 3.40403 6.51687 3.4992 6.56299 3.61055C6.60911 3.7219 6.62118 3.84443 6.59767 3.96263C6.57415 4.08084 6.51612 4.18942 6.43089 4.27464C6.34567 4.35987 6.23709 4.4179 6.11888 4.44142C6.00068 4.46493 5.87815 4.45286 5.7668 4.40674C5.65545 4.36062 5.56028 4.28251 5.49332 4.1823C5.42636 4.08209 5.39062 3.96427 5.39062 3.84375C5.39062 3.68213 5.45483 3.52714 5.56911 3.41286C5.68339 3.29858 5.83838 3.23438 6 3.23438ZM7.125 8.53125H5.0625C4.96304 8.53125 4.86766 8.49174 4.79734 8.42142C4.72701 8.35109 4.6875 8.25571 4.6875 8.15625C4.6875 8.05679 4.72701 7.96141 4.79734 7.89108C4.86766 7.82076 4.96304 7.78125 5.0625 7.78125H5.71875V5.71875H5.34375C5.24429 5.71875 5.14891 5.67924 5.07859 5.60891C5.00826 5.53859 4.96875 5.44321 4.96875 5.34375C4.96875 5.24429 5.00826 5.14891 5.07859 5.07859C5.14891 5.00826 5.24429 4.96875 5.34375 4.96875H6.09375C6.19321 4.96875 6.28859 5.00826 6.35892 5.07859C6.42924 5.14891 6.46875 5.24429 6.46875 5.34375V7.78125H7.125C7.22446 7.78125 7.31984 7.82076 7.39017 7.89108C7.46049 7.96141 7.5 8.05679 7.5 8.15625C7.5 8.25571 7.46049 8.35109 7.39017 8.42142C7.31984 8.49174 7.22446 8.53125 7.125 8.53125Z"
                            fill="#585A6B"
                          />
                        </svg>

                        <div className="pb-2 invisible group-hover:visible absolute bottom-full w-screen max-w-[210px]">
                          <div className="bg-[#30344A] shadow shadow-[rgba(0,_0,_0,_0.10)] p-3 text-[12px] text-[#F7F7F7] rounded-lg">
                            Proposal plan for AI Agent utilities
                          </div>
                        </div>
                      </div>
                    </label>
                    <input
                      role="presentation"
                      autoComplete="off"
                      id="proposalLink"
                      type="text"
                      value={newCoin.proposalLink || ''}
                      onChange={handleChange}
                      className={twMerge(
                        `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded h-12 text-[#E8E9EE] bg-transparent`,
                        // errors.name && "border-red-700"
                      )}
                    />
                  </div>
                  <div className="flex justify-between items-center w-full gap-4 flex-col md:flex-row">
                    <div className="flex-1 w-full">
                      <label
                        htmlFor="name"
                        className="text-[12px] font-medium text-[#84869A]"
                      >
                        TOKEN NAME <span className="text-red-700">*</span>
                      </label>
                      <input
                        role="presentation"
                        autoComplete="off"
                        id="name"
                        type="text"
                        value={newCoin.name || ''}
                        onChange={handleChange}
                        className={twMerge(
                          `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded h-12 text-[#E8E9EE] bg-transparent`,
                          // errors.name && "border-red-700"
                        )}
                      />
                    </div>

                    <div className="flex-1 w-full">
                      <label
                        htmlFor="ticker"
                        className="text-[12px] font-medium text-[#84869A]"
                      >
                        TOKEN SYMBOL <span className="text-red-700">*</span>
                      </label>{' '}
                      <input
                        role="presentation"
                        autoComplete="off"
                        id="ticker"
                        type="text"
                        value={newCoin.ticker || ''}
                        onChange={handleChange}
                        className={twMerge(
                          `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded h-12 text-[#E8E9EE] bg-transparent`,
                          // errors.ticker && "border-red-700"
                        )}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="text-[12px] font-medium text-[#84869A]"
                    >
                      TOKEN DESCRIPTION <span className="text-red-700">*</span>
                    </label>
                    <textarea
                      id="description"
                      value={newCoin.description || ''}
                      onChange={handleChange}
                      rows={4}
                      className={twMerge(
                        `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded text-[#E8E9EE] bg-transparent`,
                        // errors.description && "border-red-700"
                      )}
                    />
                  </div>

                  <div className="w-full flex flex-col justify-between gap-3">
                    <div className="w-full justify-between flex flex-col xs:flex-row items-start xs:items-center gap-2">
                      <label
                        htmlFor="fileUpload"
                        className="uppercase block text-[12px] font-medium text-[#84869A]"
                      >
                        Upload Image <span className="text-red-700">*</span>
                      </label>
                    </div>
                    <DropzoneFile
                      onDrop={onDrop}
                      file={imageFile}
                      setFile={(file: File) => {
                        setSelectedFileName(file?.name);
                        setImageFile(file);
                        setImagePreview(!file ? '' : URL.createObjectURL(file));
                        setIamgeUrl(!file ? '' : URL.createObjectURL(file));
                      }}
                    />
                  </div>

                  <div
                    className="flex cursor-pointer"
                    onClick={() => setShowOptional(!showOptional)}
                  >
                    More options{' '}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M19.2071 8.24992C18.8166 7.8594 18.1834 7.8594 17.7929 8.24992L12 14.0428L6.20711 8.24992C5.81658 7.8594 5.18342 7.8594 4.79289 8.24992C4.40237 8.64044 4.40237 9.27361 4.79289 9.66413L10.5858 15.457C11.3668 16.2381 12.6332 16.2381 13.4142 15.457L19.2071 9.66414C19.5976 9.27361 19.5976 8.64045 19.2071 8.24992Z"
                        fill="#9192A0"
                      />
                    </svg>
                  </div>

                  {showOptional && (
                    <div>
                      <div className="flex justify-between items-center w-full gap-4 flex-col md:flex-row">
                        <div className="flex-1 w-full">
                          <label
                            htmlFor="name"
                            className="text-[12px] font-medium text-[#84869A]"
                          >
                            TWITTER
                          </label>
                          <input
                            role="presentation"
                            autoComplete="off"
                            id="twitter"
                            type="text"
                            value={newCoin.twitter || ''}
                            onChange={handleChange}
                            className={twMerge(
                              `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded h-12 text-[#E8E9EE] bg-transparent`,
                              // errors.name && "border-red-700"
                            )}
                          />
                        </div>

                        <div className="flex-1 w-full">
                          <label
                            htmlFor="ticker"
                            className="text-[12px] font-medium text-[#84869A]"
                          >
                            TELEGRAM
                          </label>{' '}
                          <input
                            role="presentation"
                            autoComplete="off"
                            id="telegram"
                            type="text"
                            value={newCoin.telegram || ''}
                            onChange={handleChange}
                            className={twMerge(
                              `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded h-12 text-[#E8E9EE] bg-transparent`,
                            )}
                          />
                        </div>
                      </div>

                      <div className="flex justify-between items-center w-full mt-4 gap-4 flex-col md:flex-row">
                        <div className="flex-1 w-full">
                          <label
                            htmlFor="name"
                            className="text-[12px] font-medium text-[#84869A]"
                          >
                            DISCORD
                          </label>
                          <input
                            role="presentation"
                            autoComplete="off"
                            id="discord"
                            type="text"
                            value={newCoin.discord || ''}
                            onChange={handleChange}
                            className={twMerge(
                              `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded h-12 text-[#E8E9EE] bg-transparent`,
                              // errors.name && "border-red-700"
                            )}
                          />
                        </div>

                        <div className="flex-1 w-full">
                          <label
                            htmlFor="ticker"
                            className="text-[12px] font-medium text-[#84869A]"
                          >
                            WEBSITE
                          </label>{' '}
                          <input
                            role="presentation"
                            autoComplete="off"
                            id="website"
                            type="text"
                            value={newCoin.website || ''}
                            onChange={handleChange}
                            className={twMerge(
                              `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded h-12 text-[#E8E9EE] bg-transparent`,
                              // errors.ticker && "border-red-700"
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    disabled={
                      !formValid ||
                      isLoading ||
                      !wallet.publicKey ||
                      !!isSelectedAgentHasCoin ||
                      (selectedAgent?.typeAgent === 1 &&
                        Object.values(errors).some(Boolean))
                    }
                    // onClick={createCoin}
                    onClick={onLaunch}
                    // onClick={() => setShowModalPreSale(true)}
                    className="disabled:opacity-75 disabled:cursor-not-allowed uppercase p-1 rounded border-[2px] border-solid border-[rgba(255,255,255,0.25)] cursor-pointer hover:border-[rgba(255,255,255)] transition-all ease-in duration-150"
                  >
                    <div className="uppercase rounded bg-white px-6 py-2 text-[#080A14] flex items-center justify-center w-full">
                      {isLoadingConfirm && <img src={LoadingImg} />}
                      &nbsp;NEXT
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {stepCreate.id === STEP_TOKEN_CREATION[1].id && (
          <WaitingForUpcomingToken
            coin={creatingToken}
            setStepCreate={setStepCreate}
          />
        )}
        {stepCreate.id === STEP_TOKEN_CREATION[2].id && (
          <SetupInvestmentStrategy
            coin={creatingToken}
            onCreateSuccess={setCreatedTokenSuccessful}
          />
        )}
      </div>
    </div>
  );
}
